import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Sign-in — one calm page, no tabs (`studio/07`).
 *
 * An email field; under it, two ways in for that email — **send me a link** and **use a
 * password** (which reveals the password field, with *sign in* and *create an account*
 * serving the same field, plus *forgot it?*); and, apart from those, **continue with
 * Google**, with Google's mark on it. The Studio and the admin app both wear this: it holds
 * the flow and the states, and **no copy** — every string is a prop, because the Studio is EN + TR
 * and the admin app is English, and neither catalog belongs in the design system.
 *
 * Every row fills the card (`i-0001`): the buttons share a row evenly, a lone one takes it
 * all, and the secondary ways out sit centred beneath rather than ragged beside.
 *
 * Nothing here talks to Firebase. The page is handed five async actions and one function
 * that turns whatever they throw into a line a person can read (and, sometimes, a way in to
 * offer — "that address already signs in with a link"). The one flow-specific edge, a link
 * opened in a browser that never asked for it, is handled here rather than in each app: it
 * asks for the address again instead of failing.
 */
import { useEffect, useRef, useState } from 'react';
import { Button } from './Button.js';
import { Field } from './Field.js';
import { GoogleMark } from './GoogleMark.js';
import { TextLink } from './TextLink.js';
/** Firebase's own default minimum. A console policy that asks for more wins, and says so. */
export const PASSWORD_MIN_LENGTH = 8;
export function SignInPanel({ strings: s, actions, isLinkArrival, pendingEmail, children, }) {
    const [email, setEmail] = useState(pendingEmail ?? '');
    const [password, setPassword] = useState('');
    const [mode, setMode] = useState('choose');
    const [busy, setBusy] = useState(null);
    const [problem, setProblem] = useState(null);
    const [emailError, setEmailError] = useState(undefined);
    const [passwordError, setPasswordError] = useState(undefined);
    // The link was opened in the browser that asked for it: finish without asking again.
    const autoTried = useRef(false);
    useEffect(() => {
        if (!isLinkArrival || pendingEmail === null || autoTried.current)
            return;
        autoTried.current = true;
        setBusy('finish');
        actions.completeLink(pendingEmail).catch(async (error) => {
            setBusy(null);
            setProblem(await actions.describe(error));
        });
    }, [isLinkArrival, pendingEmail, actions]);
    function cleanEmail() {
        const value = email.trim();
        if (value === '') {
            setEmailError(s.emailRequired);
            return null;
        }
        setEmailError(undefined);
        return value;
    }
    async function run(kind, work) {
        setProblem(null);
        setBusy(kind);
        try {
            await work();
            return true;
        }
        catch (error) {
            const described = await actions.describe(error);
            setProblem(described);
            if (described.email !== undefined)
                setEmail(described.email);
            if (described.offer === 'password')
                setMode('password');
            if (described.offer === 'link')
                setMode('choose');
            return false;
        }
        finally {
            setBusy(null);
        }
    }
    async function onSendLink() {
        const value = cleanEmail();
        if (value === null)
            return;
        if (await run('link', () => actions.sendLink(value)))
            setMode('sent');
    }
    async function onSignIn() {
        const value = cleanEmail();
        if (value === null)
            return;
        if (password === '') {
            setPasswordError(s.passwordRequired);
            return;
        }
        setPasswordError(undefined);
        await run('signIn', () => actions.signInWithPassword(value, password));
    }
    async function onCreate() {
        const value = cleanEmail();
        if (value === null)
            return;
        if (password.length < PASSWORD_MIN_LENGTH) {
            // Refused here, before the network: the rule is stated, not discovered.
            setPasswordError(s.passwordTooShort);
            return;
        }
        setPasswordError(undefined);
        await run('create', () => actions.createAccount(value, password));
    }
    async function onReset() {
        const value = cleanEmail();
        if (value === null)
            return;
        setPasswordError(undefined);
        if (await run('reset', () => actions.resetPassword(value)))
            setMode('resetSent');
    }
    async function onGoogle() {
        await run('google', () => actions.signInWithGoogle());
    }
    async function onFinish() {
        const value = cleanEmail();
        if (value === null)
            return;
        await run('finish', () => actions.completeLink(value));
    }
    function primaryFor(mode) {
        return mode === 'password' ? onSignIn : onSendLink;
    }
    const heading = (text) => (_jsx("h1", { className: "story", style: { fontSize: 'var(--t-d2)' }, children: text }));
    const problemLine = problem === null ? null : (_jsxs("p", { className: "signin-problem", role: "alert", children: [problem.message, problem.offer === 'password' && mode !== 'password' && (_jsxs(_Fragment, { children: [' ', _jsx(TextLink, { onClick: () => setMode('password'), children: s.offerPassword })] })), problem.offer === 'link' && (_jsxs(_Fragment, { children: [' ', _jsx(TextLink, { onClick: () => void onSendLink(), children: s.offerLink })] }))] }));
    if (isLinkArrival) {
        return (_jsxs("div", { className: "signin", children: [children, busy === 'finish' ? (_jsx("p", { className: "note", role: "status", children: s.finishing })) : (_jsxs(_Fragment, { children: [heading(s.confirmTitle), _jsx("p", { className: "note", children: s.confirmBody }), _jsx(Field, { label: s.email, type: "email", autoComplete: "email", placeholder: s.emailPlaceholder, value: email, ...(emailError !== undefined ? { error: emailError } : {}), onChange: setEmail, onKeyDown: (event) => {
                                if (event.key === 'Enter')
                                    void onFinish();
                            } }), problemLine, _jsx("div", { className: "signin-acts", children: _jsx(Button, { tone: "primary", size: "lg", onClick: () => void onFinish(), children: s.confirmAction }) })] }))] }));
    }
    if (mode === 'sent') {
        return (_jsxs("div", { className: "signin", children: [children, heading(s.sentTitle), _jsx("p", { className: "note", children: s.sentBody(email.trim()) }), s.emulator !== undefined && _jsx("p", { className: "cost", children: s.emulator }), _jsx("div", { className: "signin-alt", children: _jsx(TextLink, { onClick: () => setMode('choose'), children: s.sentAgain }) })] }));
    }
    if (mode === 'resetSent') {
        return (_jsxs("div", { className: "signin", children: [children, heading(s.resetTitle), _jsx("p", { className: "note", children: s.resetBody(email.trim()) }), s.emulator !== undefined && _jsx("p", { className: "cost", children: s.emulator }), _jsx("div", { className: "signin-alt", children: _jsx(TextLink, { onClick: () => setMode('password'), children: s.back }) })] }));
    }
    return (_jsxs("div", { className: "signin", children: [children, heading(s.title), _jsx("p", { className: "note", children: s.lead }), _jsx(Field, { label: s.email, type: "email", autoComplete: "email", placeholder: s.emailPlaceholder, value: email, ...(emailError !== undefined ? { error: emailError } : {}), onChange: setEmail, onKeyDown: (event) => {
                    if (event.key === 'Enter')
                        void primaryFor(mode)();
                } }), mode === 'password' && (_jsx(Field, { label: s.password, type: "password", autoComplete: "current-password", value: password, ...(passwordError !== undefined ? { error: passwordError } : {}), onChange: setPassword, onKeyDown: (event) => {
                    if (event.key === 'Enter')
                        void onSignIn();
                } })), problemLine, mode === 'choose' ? (_jsxs("div", { className: "signin-acts", children: [_jsx(Button, { tone: "primary", size: "lg", working: busy === 'link', isDisabled: busy !== null, onClick: () => void onSendLink(), children: busy === 'link' ? s.sending : s.sendLink }), _jsx(Button, { size: "lg", isDisabled: busy !== null, onClick: () => setMode('password'), children: s.usePassword })] })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: "signin-acts", children: [_jsx(Button, { tone: "primary", size: "lg", working: busy === 'signIn', isDisabled: busy !== null, onClick: () => void onSignIn(), children: busy === 'signIn' ? s.working : s.signIn }), _jsx(Button, { size: "lg", working: busy === 'create', isDisabled: busy !== null, onClick: () => void onCreate(), children: busy === 'create' ? s.working : s.create })] }), _jsxs("div", { className: "signin-alt", children: [_jsx(TextLink, { isDisabled: busy !== null, onClick: () => void onReset(), children: s.forgot }), _jsx(TextLink, { isDisabled: busy !== null, onClick: () => setMode('choose'), children: s.sendLink })] })] })), _jsx("p", { className: "cap signin-or", "aria-hidden": "true", children: s.or }), _jsx("div", { className: "signin-acts", children: _jsxs(Button, { size: "lg", working: busy === 'google', isDisabled: busy !== null, onClick: () => void onGoogle(), children: [_jsx(GoogleMark, {}), busy === 'google' ? s.working : s.google] }) })] }));
}
//# sourceMappingURL=SignIn.js.map